import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Type, Image as ImageIcon, ArrowRight, ArrowLeft, Check, Lock, Target } from "lucide-react";
import api from "../lib/api";
import { useRoastLitConfig, catMeta } from "../lib/verdictly";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STEPS = ["Category", "Content", "Audience", "Preview", "Package"];

export default function CreateTask() {
  const navigate = useNavigate();
  const cfg = useRoastLitConfig();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(null);
  const [contentType, setContentType] = useState("text");
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [audience, setAudience] = useState({});
  const [pkg, setPkg] = useState("human");
  const [submitting, setSubmitting] = useState(false);

  const categories = cfg?.categories || {};
  const packages = cfg?.packages || {};
  const audienceAttrs = cfg?.audience || {};
  const threshold = cfg?.targeting_threshold ?? 2;
  const symbol = cfg?.symbol || "₹";
  const cat = category ? categories[category] : null;
  const audienceCount = Object.values(audience).filter(Boolean).length;

  // Selecting any audience attribute implies the Targeted package.
  useEffect(() => { if (audienceCount > 0) setPkg("targeted"); }, [audienceCount]);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4MB");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(f);
  };

  const toggleAudience = (attr, opt) => {
    setAudience((prev) => {
      const next = { ...prev };
      if (next[attr] === opt) delete next[attr];
      else {
        const active = Object.values({ ...next, [attr]: opt }).filter(Boolean).length;
        if (!prev[attr] && active > threshold) {
          toast.error(`Target at most ${threshold} attributes — it keeps your panel fillable.`);
          return prev;
        }
        next[attr] = opt;
      }
      return next;
    });
  };

  const canNext = () => {
    if (step === 0) return !!category;
    if (step === 1) return contentType === "text" ? !!text.trim() : !!image;
    return true;
  };

  const next = () => { if (!canNext()) return toast.error("Fill this step first."); setStep((s) => Math.min(s + 1, 4)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const pay = async () => {
    setSubmitting(true);
    try {
      const co = (await api.post("/tasks/checkout", {
        category, content_type: contentType,
        content: contentType === "text" ? text : image,
        package: pkg, audience: pkg === "targeted" ? audience : {},
      })).data;
      const finalize = async (payment) => {
        const d = (await api.post("/tasks/confirm", { draft_id: co.draft_id, ...payment })).data;
        if (d.status === "rejected") return toast.error("Blocked by moderation — that content crosses the guidelines.");
        toast.success("You're live! Your panel is being matched. 🎯");
        navigate("/app/tasks");
      };
      if (co.mock) return await finalize({});
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment window");
      const rzp = new window.Razorpay({
        key: co.key_id, amount: co.amount_minor, currency: co.currency, order_id: co.order_id,
        name: "RoastLit", description: packages[pkg]?.name,
        prefill: { name: co.name, email: co.email }, theme: { color: "#ff4500" },
        handler: async (r) => { try { await finalize({ payment_id: r.razorpay_payment_id, order_id: r.razorpay_order_id, signature: r.razorpay_signature }); } catch (e) { toast.error("Payment captured but confirmation failed."); } },
        modal: { ondismiss: () => { setSubmitting(false); toast("Payment cancelled."); } },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  if (!cfg) return <div className="text-zinc-400">Loading…</div>;

  return (
    <div className="max-w-2xl" data-testid="create-task">
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Get your verdict</h1>
      <p className="text-zinc-400 mt-2">Ask 5 strangers. Get the truth.</p>

      {/* stepper */}
      <div className="flex items-center gap-1.5 mt-6 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className="h-1 rounded-full transition-colors" style={{ background: i <= step ? "#ff4500" : "rgba(255,255,255,0.12)" }} />
            <div className={`mt-1.5 text-[10px] uppercase tracking-widest ${i === step ? "text-brand font-bold" : "text-zinc-600"}`}>{s}</div>
          </div>
        ))}
      </div>

      {/* STEP 0 — category */}
      {step === 0 && (
        <div className="grid gap-3" data-testid="step-category">
          {Object.values(categories).map((c) => {
            const m = catMeta(c.key); const Icon = m.icon; const active = category === c.key;
            return (
              <button key={c.key} data-testid={`cat-${c.key}`} onClick={() => setCategory(c.key)}
                className="text-left rounded-xl border p-5 transition-all hover:-translate-y-0.5"
                style={{ borderColor: active ? m.accent : "rgba(255,255,255,0.1)", background: active ? m.soft : "rgba(24,24,27,0.6)", boxShadow: active ? `0 0 24px ${m.accent}33` : "none" }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.soft }}>
                    <Icon size={22} style={{ color: m.accent }} />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-xl">{c.emoji} {c.name}</div>
                    <div className="text-sm text-zinc-400">{c.tagline}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* STEP 1 — content */}
      {step === 1 && cat && (
        <div className="space-y-4" data-testid="step-content">
          <div className="text-sm text-zinc-300">{cat.prompt}</div>
          <div className="grid grid-cols-2 gap-3">
            {[{ id: "text", label: "Text", icon: Type }, { id: "image", label: "Image", icon: ImageIcon }].map((c) => (
              <button key={c.id} data-testid={`ctype-${c.id}`} onClick={() => setContentType(c.id)}
                className={`flex items-center gap-2 justify-center py-3 rounded-sm border transition-colors ${contentType === c.id ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                <c.icon size={16} /> {c.label}
              </button>
            ))}
          </div>
          {contentType === "text" ? (
            <textarea data-testid="task-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={800}
              placeholder={cat.prompt}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
          ) : (
            <div>
              <input data-testid="task-image" type="file" accept="image/*" onChange={onFile} className="text-sm text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-brand file:text-black file:font-semibold" />
              {image && <img src={image} alt="preview" className="mt-3 max-h-56 rounded-sm border border-white/10" />}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 — audience */}
      {step === 2 && (
        <div className="space-y-5" data-testid="step-audience">
          <div className="rounded-lg border border-white/10 bg-zinc-900/50 p-4 flex items-start gap-3">
            <Target size={18} className="text-brand mt-0.5 shrink-0" />
            <div className="text-sm text-zinc-300">
              Want a specific audience? Pick up to <b>{threshold}</b> attributes.
              <span className="text-zinc-500"> Targeting unlocks with the Targeted package ({symbol}{packages.targeted?.price}). Leave blank for the fastest, cheapest panel.</span>
            </div>
          </div>
          {Object.entries(audienceAttrs).map(([attr, def]) => (
            <div key={attr}>
              <div className="text-xs tracking-widest uppercase text-zinc-400 font-bold mb-2">{def.label}</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(def.options).map(([opt, label]) => (
                  <button key={opt} data-testid={`aud-${attr}-${opt}`} onClick={() => toggleAudience(attr, opt)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${audience[attr] === opt ? "border-brand bg-brand/10 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {audienceCount > 0 && (
            <div data-testid="targeting-note" className="text-xs text-brand-accent flex items-center gap-1.5">
              <Lock size={12} /> {audienceCount} attribute{audienceCount > 1 ? "s" : ""} selected — Targeted package applied.
            </div>
          )}
        </div>
      )}

      {/* STEP 3 — questions preview */}
      {step === 3 && cat && (
        <div className="space-y-4" data-testid="step-preview">
          <div className="text-sm text-zinc-300">Here's exactly what your {cat.emoji} {cat.name} panel will answer:</div>
          <div className="space-y-2">
            {cat.questions.map((q, i) => (
              <div key={q.id} data-testid={`preview-q-${q.id}`} className="rounded-lg border border-white/10 bg-zinc-900/50 p-4 flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-white/10 text-xs flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                <div>
                  <div className="text-sm text-zinc-100">{q.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {q.type === "scale" ? `Rating ${q.min}–${q.max}` : q.type === "choice" ? Object.values(q.option_labels || {}).join(" / ") : "Written answer"}
                    {!q.required && " · optional"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* STEP 4 — package + pay */}
      {step === 4 && (
        <div className="space-y-4" data-testid="step-package">
          <div className="grid gap-3">
            {Object.values(packages).map((p) => {
              const active = pkg === p.key;
              const disabled = false;
              return (
                <button key={p.key} data-testid={`pkg-${p.key}`} disabled={disabled} onClick={() => { setPkg(p.key); if (p.key !== "targeted") setAudience({}); }}
                  className="text-left rounded-xl border p-5 transition-all disabled:opacity-40"
                  style={{ borderColor: active ? "#ff4500" : "rgba(255,255,255,0.1)", background: active ? "rgba(255,69,0,0.08)" : "rgba(24,24,27,0.6)" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-heading font-bold text-lg flex items-center gap-2">
                        {p.name} {p.targeting && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-accent/20 text-brand-accent tracking-widest uppercase">Targeting</span>}
                      </div>
                      <div className="text-sm text-zinc-400">{p.reviewers} reviewers · {p.desc}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-bold text-2xl">{symbol}{p.price}</div>
                      {active && <Check size={16} className="text-brand ml-auto mt-1" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {pkg !== "targeted" && audienceCount > 0 && (
            <div className="text-xs text-zinc-500">Targeting needs the Targeted package — your audience filters will be cleared for this package.</div>
          )}
          <button data-testid="task-pay-btn" onClick={pay} disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
            {submitting ? "Processing…" : <>Pay {symbol}{packages[pkg]?.price} & go live <ArrowRight size={16} /></>}
          </button>
          <p className="text-[11px] text-zinc-600 text-center">Mock payment for testing — no real charge.</p>
        </div>
      )}

      {/* nav */}
      {step < 4 && (
        <div className="flex items-center justify-between mt-8">
          <button data-testid="step-back" onClick={back} disabled={step === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors disabled:opacity-30">
            <ArrowLeft size={16} /> Back
          </button>
          <button data-testid="step-next" onClick={next}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
            {step === 3 ? "Choose package" : "Next"} <ArrowRight size={16} />
          </button>
        </div>
      )}
      {step === 4 && (
        <div className="mt-4">
          <button data-testid="step-back" onClick={back} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      )}
    </div>
  );
}
